import { ReactNode, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { matchPath, useLocation } from 'react-router-dom';
import { Routes } from '../../../app/routes';
import { selectIsAuthenticated } from '../../../modules/auth/store/auth-slice';
import SignRequestsSubmenuContent from '../../../modules/sign-requests/components/sign-requests-submenu-content/sign-requests-submenu-content';
import { useGetUserQuery } from '../../../modules/user/api/user-api';
import { UserPermissionType } from '../../../modules/user/models/user-permission-type';
import { CustomerIcon } from '../../../shared/icons/customer-icon';
import { DocumentIcon } from '../../../shared/icons/document-icon';
import { DocumentImageIcon } from '../../../shared/icons/document-image-icon';
import { GroupIcon } from '../../../shared/icons/group-icon';
import { SpeedIcon } from '../../../shared/icons/speed-icon';
import { UsersIcon } from '../../../shared/icons/users-icon';
import { hasPermission } from '../../../shared/utils/has-permission';
import { useAppSelector } from '../../../store/hooks';

export interface MenuItem {
  key: string;
  path: string;
  label: ReactNode;
  icon: ReactNode;
  subMenuContent?: ReactNode;
}

const useMenuItems = (): [MenuItem[], MenuItem | undefined] => {
  const { t } = useTranslation('common');
  const { pathname } = useLocation();
  const isAuthenticated = useAppSelector(selectIsAuthenticated);
  const { data: currentUser } = useGetUserQuery(undefined, {
    skip: !isAuthenticated,
  });

  const menuItems: MenuItem[] = useMemo(
    () =>
      currentUser
        ? [
            ...(hasPermission(currentUser, UserPermissionType.ViewDashboard)
              ? [
                  {
                    key: 'dashboard',
                    path: Routes.Dashboard,
                    label: t('menu.dashboard'),
                    icon: <SpeedIcon />,
                  },
                ]
              : []),
            ...(hasPermission(currentUser, UserPermissionType.ViewDocuments)
              ? [
                  {
                    key: 'sign-requests',
                    path: Routes.SignRequests,
                    label: t('menu.signRequests'),
                    icon: <DocumentIcon />,
                    subMenuContent: <SignRequestsSubmenuContent />,
                  },
                ]
              : []),
            ...(hasPermission(currentUser, UserPermissionType.ViewDocuments)
              ? [
                  {
                    key: 'scan-requests',
                    path: Routes.ScanRequests,
                    label: t('menu.scanRequests'),
                    icon: <DocumentImageIcon />,
                  },
                ]
              : []),
            ...(hasPermission(currentUser, UserPermissionType.ViewCustomers)
              ? [
                  {
                    key: 'customers',
                    path: Routes.Customers,
                    label: t('menu.customers'),
                    icon: <CustomerIcon />,
                  },
                ]
              : []),
            ...(hasPermission(currentUser, UserPermissionType.ViewUsers)
              ? [
                  {
                    key: 'clientUsers',
                    path: Routes.ClientUsers,
                    label: t('menu.clientUsers'),
                    icon: <UsersIcon />,
                  },
                ]
              : []),
            ...(hasPermission(currentUser, UserPermissionType.ViewGroups)
              ? [
                  {
                    key: 'clientGroups',
                    path: Routes.ClientGroups,
                    label: t('menu.clientGroups'),
                    icon: <GroupIcon />,
                  },
                ]
              : []),
          ]
        : [],
    [currentUser, t],
  );

  const activeMenuItem = useMemo(
    () => menuItems.find(item => matchPath({ path: item.path }, pathname)),
    [pathname, menuItems],
  );

  return [menuItems, activeMenuItem];
};

export default useMenuItems;
